import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Field, NativeSelect } from "@/components/common/Page";
import { Spinner } from "@/components/common/States";
import { useFetch } from "@/hooks/useFetch";
import { api, errMsg } from "@/lib/api";
import { todayISO } from "@/lib/format";

export const ClassFormDialog = ({ open, onOpenChange, lesson, defaults = {}, onSaved }) => {
  const students = useFetch("/students?status=ativo", { skip: !open });
  const profile = useFetch("/profile", { skip: !open });
  const subjects = useFetch("/subjects", { skip: !open });
  const [form, setForm] = useState({});
  const [repeat, setRepeat] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    setError("");
    setRepeat(false);
    if (lesson) {
      const { student_id, subject, date, start_time, duration, type, price, notes } = lesson;
      setForm({ student_id, subject, date, start_time, duration, type, price, notes: notes || "", repeat_weeks: 4 });
    } else {
      setForm({ student_id: defaults.student_id || "", subject: "", date: defaults.date || todayISO(), start_time: defaults.start_time || "14:00",
        duration: "", type: "presencial", price: "", notes: "", repeat_weeks: 4 });
    }
  }, [open, lesson]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (open && !lesson && profile.data) {
      setForm((f) => ({ ...f, duration: f.duration || profile.data.default_duration || 60, price: f.price === "" ? profile.data.default_price ?? 0 : f.price,
        type: profile.data.modality === "online" ? "online" : f.type }));
    }
  }, [profile.data, open, lesson]);

  const set = (k) => (e) => {
    const value = e.target.value;
    setForm((f) => {
      const next = { ...f, [k]: value };
      if (k === "student_id" && !f.subject) {
        const st = students.data?.find((s) => s.id === value);
        if (st?.subjects?.length) next.subject = st.subjects[0];
      }
      return next;
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    const payload = { ...form, duration: Number(form.duration), price: Number(form.price || 0), notes: form.notes || null,
      repeat_weeks: repeat ? Number(form.repeat_weeks) - 1 : 0 };
    try {
      if (lesson) {
        await api.put(`/classes/${lesson.id}`, payload);
        toast.success("Aula atualizada");
      } else {
        const { data } = await api.post("/classes", payload);
        toast.success(data.created.length > 1 ? `${data.created.length} aulas agendadas` : "Aula agendada");
        if (data.skipped.length) toast.warning(`${data.skipped.length} data(s) ignorada(s) por conflito de horário`);
      }
      onSaved?.();
      onOpenChange(false);
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-xl" data-testid="class-form-dialog">
        <DialogHeader><DialogTitle>{lesson ? "Editar / reagendar aula" : "Nova aula"}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="grid gap-4 sm:grid-cols-2">
          <Field label="Aluno *" className="sm:col-span-2">
            <NativeSelect value={form.student_id || ""} onChange={set("student_id")} required data-testid="class-student-select">
              <option value="">Selecione o aluno</option>
              {(students.data || []).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </NativeSelect>
          </Field>
          <Field label="Disciplina *" className="sm:col-span-2">
            <Input list="subject-options" value={form.subject || ""} onChange={set("subject")} required data-testid="class-subject-input" />
            <datalist id="subject-options">{(subjects.data || []).map((s) => <option key={s.id} value={s.name} />)}</datalist>
          </Field>
          <Field label="Data *"><Input type="date" value={form.date || ""} onChange={set("date")} required data-testid="class-date-input" /></Field>
          <Field label="Horário *"><Input type="time" value={form.start_time || ""} onChange={set("start_time")} required data-testid="class-time-input" /></Field>
          <Field label="Duração (min) *"><Input type="number" min="15" max="480" step="5" value={form.duration ?? ""} onChange={set("duration")} required data-testid="class-duration-input" /></Field>
          <Field label="Valor (R$)"><Input type="number" min="0" step="0.01" value={form.price ?? ""} onChange={set("price")} data-testid="class-price-input" /></Field>
          <Field label="Tipo">
            <NativeSelect value={form.type || "presencial"} onChange={set("type")} data-testid="class-type-select">
              <option value="presencial">Presencial</option>
              <option value="online">Online</option>
            </NativeSelect>
          </Field>
          {!lesson && (
            <div className="flex items-end gap-3">
              <label className="flex h-10 items-center gap-2 text-sm">
                <Checkbox checked={repeat} onCheckedChange={(v) => setRepeat(!!v)} data-testid="class-repeat-checkbox" /> Repetir semanalmente
              </label>
              {repeat && <Input type="number" min="2" max="52" className="w-20" value={form.repeat_weeks} onChange={set("repeat_weeks")} data-testid="class-repeat-weeks-input" aria-label="Quantidade de semanas" />}
            </div>
          )}
          <Field label="Observações" className="sm:col-span-2"><Textarea rows={2} value={form.notes || ""} onChange={set("notes")} data-testid="class-notes-input" /></Field>
          {repeat && <p className="text-xs text-muted-foreground sm:col-span-2">Serão criadas {form.repeat_weeks} aulas, uma por semana. Datas com conflito serão ignoradas.</p>}
          {error && <p className="text-sm text-destructive sm:col-span-2" data-testid="class-form-error">{error}</p>}
          <DialogFooter className="sm:col-span-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} data-testid="class-form-cancel">Cancelar</Button>
            <Button type="submit" disabled={busy} data-testid="class-form-submit">{busy && <Spinner className="mr-2" />}Salvar aula</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
